import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { forEach, get, map, pick } from 'lodash';

export interface DatabaseItem {
  id: string;
  name: string;
}

export interface TableItem {
  id: string;
  name: string;
  database: string;
  columns: unknown[];
  scanned?: boolean;
}

type ProbeDataItem = {
  id: string;
  name: string;
  children?: {
    id: string;
    name: string;
    children?: unknown[];
    scanned?: boolean;
  }[];
};

function buildNormalizedTreeFromProbeData(probeData: ProbeDataItem[]) {
  const databasesIds: string[] = [];
  const databases: Record<string, DatabaseItem> = {};
  const tables: Record<string, TableItem> = {};
  const tablesForDatabase: Record<string, string[]> = {};
  forEach(probeData, (d) => {
    const database: DatabaseItem = { id: d.id, name: d.name };
    databasesIds.push(d.id);
    databases[d.id] = database;
    tablesForDatabase[d.id] = [];
    forEach(d.children ?? [], (t) => {
      const table: TableItem = {
        id: t.id,
        name: t.name,
        database: d.name,
        columns: map(t.children ?? [], (c) => ({
          ...(c as Record<string, unknown>),
        })),
        scanned: t.scanned,
      };
      tables[t.id] = table;
      tablesForDatabase[d.id].push(t.id);
    });
  });
  return { databasesIds, databases, tables, tablesForDatabase };
}

export interface UseSqlRunnerDatabaseTreeParams {
  probeData: unknown[] | null;
  probeDataFlat: Record<string, unknown> | null;
  probeSample: unknown;
  sourceConfig: Record<string, unknown>;
  sourceBeingEdited: Record<string, unknown> | null;
  selectedDatabase: string | null;
  selectedTable: { id: string; name: string; columns?: unknown[] } | null;
  selectedCredentialId: string | null;
  onSourceConfigChanged: (config: Record<string, unknown>) => void;
  onTableSelected: (
    table: { id: string; name: string; columns?: unknown[] } | null,
    databaseId?: string,
  ) => void;
  onRequestProbeTree: (expand?: boolean, node?: unknown) => void;
}

export interface UseSqlRunnerDatabaseTreeResult {
  databasesArray: DatabaseItem[];
  tables: Record<string, TableItem>;
  tablesForDatabase: Record<string, string[]>;
  selectedDatabaseId: string | null;
  selectDatabase: (dId: string) => void;
  expandTable: (tId: string) => void;
  effectiveSamplesData: unknown;
}

export const treeInitialState = {
  databasesIds: [] as string[],
  databases: {} as Record<string, DatabaseItem>,
  tables: {} as Record<string, TableItem>,
  tablesForDatabase: {} as Record<string, string[]>,
  selectedDatabaseId: null as string | null,
};

export function useSqlRunnerDatabaseTree(
  params: UseSqlRunnerDatabaseTreeParams,
): UseSqlRunnerDatabaseTreeResult {
  const {
    probeData,
    probeDataFlat,
    probeSample,
    sourceConfig,
    sourceBeingEdited,
    selectedDatabase,
    selectedTable,
    selectedCredentialId,
    onSourceConfigChanged,
    onTableSelected,
    onRequestProbeTree,
  } = params;

  const [databasesIds, setDatabasesIds] = useState<string[]>(treeInitialState.databasesIds);
  const [databases, setDatabases] = useState<Record<string, DatabaseItem>>(
    treeInitialState.databases,
  );
  const [tables, setTables] = useState<Record<string, TableItem>>(treeInitialState.tables);
  const [tablesForDatabase, setTablesForDatabase] = useState<Record<string, string[]>>(
    treeInitialState.tablesForDatabase,
  );
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string | null>(
    treeInitialState.selectedDatabaseId,
  );
  const [samplesData, setSamplesData] = useState<unknown>(null);

  const probeNode = useCallback(
    (nodeId: string) => {
      const node = probeDataFlat?.[nodeId] as { scanned?: boolean } | undefined;
      if (node && !node.scanned) {
        onRequestProbeTree(false, node);
      }
    },
    [probeDataFlat, onRequestProbeTree],
  );

  const selectTable = useCallback(
    (tId: string) => {
      if (tId.length === 0) {
        onTableSelected(null);
        return;
      }
      const table = tables[tId];
      if (table && Object.keys(table).length > 0) {
        probeNode(tId);
        onTableSelected(table);
      }
    },
    [tables, onTableSelected, probeNode],
  );

  const selectDatabase = useCallback(
    (dId: string) => {
      if (dId === selectedDatabaseId) {
        setSelectedDatabaseId(null);
        onTableSelected(null);
        return;
      }
      const isDatabaseNotFound = !databases[dId];
      if (isDatabaseNotFound) {
        onSourceConfigChanged({ ...sourceConfig, database: dId });
        setSelectedDatabaseId(dId);
        return;
      }
      probeNode(dId);
      setSelectedDatabaseId(dId);
      if (!sourceConfig.table) {
        onTableSelected(null, dId);
      }
    },
    [
      selectedDatabaseId,
      databases,
      sourceConfig,
      onSourceConfigChanged,
      onTableSelected,
      probeNode,
    ],
  );

  const expandTable = useCallback(
    (tId: string) => {
      probeNode(tId);
    },
    [probeNode],
  );

  const initializeState = useCallback(() => {
    const data = probeData as ProbeDataItem[] | null;
    if (!data || !Array.isArray(data) || data.length === 0) {
      return;
    }
    const {
      databasesIds: ids,
      databases: dbs,
      tables: tbls,
      tablesForDatabase: tfd,
    } = buildNormalizedTreeFromProbeData(data);
    const explicitDbId = (selectedDatabase ?? sourceConfig?.database ?? selectedDatabaseId) as
      | string
      | null;
    const selDbId = (explicitDbId ?? ids[0]) as string | null;
    setDatabasesIds(ids);
    setDatabases(dbs);
    setTables(tbls);
    setTablesForDatabase(tfd);
    setSelectedDatabaseId(selDbId);
    // When falling back to ids[0] (no saved or pre-selected database), probe it
    // so its table list is populated immediately rather than showing blank.
    if (!explicitDbId && selDbId) {
      probeNode(selDbId);
    }
    if (sourceBeingEdited && sourceConfig?.table) {
      const database = sourceConfig.database as string | undefined;
      const table = sourceConfig.table as string;
      const tableId = `${database ?? ids[0]}/${table}`;
      if (Object.keys(tbls).length > 0 && tbls[tableId]) {
        selectTable(tableId);
        const tableConfig = pick(sourceConfig, [
          'incrementing.column.name',
          'timestamp.column.name',
          'incrementing.load.from',
          'timestamp.load.from',
        ]);
        forEach(tableConfig, (value, key) => {
          onSourceConfigChanged({ ...sourceConfig, [key]: value });
        });
      }
    }
  }, [
    probeData,
    sourceConfig,
    sourceBeingEdited,
    selectedDatabase,
    selectedDatabaseId,
    selectTable,
    onSourceConfigChanged,
    probeNode,
  ]);

  const probeDataKeyRef = useRef<string>('');
  const probeDataKey =
    probeData && Array.isArray(probeData) && probeData.length > 0
      ? (
          probeData as {
            id?: string;
            children?: { id?: string; children?: unknown[] }[];
          }[]
        )
          .map((d) => {
            const tablePart = (d.children ?? [])
              .map((t) => `${t.id}:${t.children?.length ?? 0}`)
              .join(',');
            return `${d.id ?? ''}[${tablePart}]`;
          })
          .filter(Boolean)
          .join('|')
      : '';

  useEffect(() => {
    if (!selectedCredentialId) {
      probeDataKeyRef.current = '';
      setDatabasesIds(treeInitialState.databasesIds);
      setDatabases(treeInitialState.databases);
      setTables(treeInitialState.tables);
      setTablesForDatabase(treeInitialState.tablesForDatabase);
      setSelectedDatabaseId(treeInitialState.selectedDatabaseId);
      setExpandedTableId(treeInitialState.expandedTableId);
    }
  }, [selectedCredentialId]);

  useEffect(() => {
    if (!probeDataKey) return;
    if (probeDataKeyRef.current === probeDataKey) return;
    probeDataKeyRef.current = probeDataKey;
    initializeState();
  }, [probeDataKey]);

  useEffect(() => {
    const samples = get(probeSample as object, '[0].response.output');
    if (samples != null) {
      setSamplesData(samples);
    }
  }, [probeSample]);

  const effectiveSamplesData = useMemo(() => {
    const fromProbe = get(probeSample as object, '[0].response.output');
    if (fromProbe != null) return fromProbe;
    return samplesData;
  }, [probeSample, samplesData]);

  const databasesArray = useMemo(
    () => map(databasesIds, (i) => databases[i]),
    [databasesIds, databases],
  );

  return {
    databasesArray,
    tables,
    tablesForDatabase,
    selectedDatabaseId,
    selectDatabase,
    expandTable,
    effectiveSamplesData,
  };
}
